import React, { useCallback, useContext, useState } from 'react';
import Table from 'react-bootstrap/Table';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import DeleteButton from '../Generic/DeleteButton';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';
import ProtocolModal from './ProtocolModal';
import {
  deleteProtocol,
  getProtocolDetails,
} from '../../clients/protocol_client';
import {
  formatProtocolProductLabel,
  getProtocolProductCount,
  getProtocolProducts,
} from '../../utils/protocols';
import { logError } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'ProtocolTable';

/** Number of linked product chips shown before collapsing into a "+N more". */
const PRODUCT_PREVIEW_LIMIT = 3;

/**
 * Table of lab protocols. Rows are ordered by the backend (always by `name`),
 * so there are no sortable headers.
 *
 * @param {Object} props
 * @param {Array<Object>} props.protocolList - Protocol rows from `GET /protocols/`.
 * @param {Function} props.handleDelete - Refresh the list after a delete/update.
 * @param {Function} [props.clearSearchValue] - Clears the page search input.
 */
const ProtocolTable = ({ protocolList, handleDelete, clearSearchValue }) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  // List rows carry `product_count` but not `products[]`; hydrate on hover so
  // the edit modal opens with the picker already populated.
  const [protocolsWithFullData, setProtocolsWithFullData] = useState({});
  const [loadingProtocols, setLoadingProtocols] = useState({});

  const fetchFullProtocolData = useCallback(
    async protocolId => {
      if (loadingProtocols[protocolId] || protocolsWithFullData[protocolId]) {
        return;
      }

      setLoadingProtocols(prev => ({ ...prev, [protocolId]: true }));

      const result = await getProtocolDetails(token, protocolId);

      if (result?.success) {
        setProtocolsWithFullData(prev => ({
          ...prev,
          [protocolId]: result.data,
        }));
      } else {
        logError(LOG_CONTEXT, 'fetchFullProtocolData failed', {
          protocolId,
          status: result?.status,
          messages: result?.messages,
        });
      }

      setLoadingProtocols(prev => ({ ...prev, [protocolId]: false }));
    },
    [token, loadingProtocols, protocolsWithFullData]
  );

  const getProtocolForEditing = protocol =>
    protocolsWithFullData[protocol.id] || protocol;

  /**
   * Drop the cached detail payload so the next hover refetches it.
   *
   * @param {string|number} protocolId
   */
  const invalidateProtocol = protocolId => {
    setProtocolsWithFullData(prev => {
      const next = { ...prev };
      delete next[protocolId];
      return next;
    });
  };

  const renderProductPreview = protocol => {
    const products = getProtocolProducts(
      protocolsWithFullData[protocol.id] || protocol
    );
    const count = getProtocolProductCount(protocol);

    if (count === 0) {
      return <span className="text-muted">No products linked</span>;
    }
    if (products.length === 0) {
      return <span>{count}</span>;
    }

    const preview = products.slice(0, PRODUCT_PREVIEW_LIMIT);
    const remaining = products.length - preview.length;

    return (
      <div className="d-flex flex-column align-items-center">
        <span className="fw-semibold">{count}</span>
        <small className="text-muted">
          {preview.map(formatProtocolProductLabel).join(', ')}
          {remaining > 0 ? ` +${remaining} more` : ''}
        </small>
      </div>
    );
  };

  return (
    <Table striped bordered hover>
      <thead>
        <tr className="text-center">
          <th>#</th>
          <th>Name</th>
          <th>Products</th>
          <th>Link</th>
          {!isViewer ? <th>Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {protocolList.map((protocol, index) => (
          <tr key={protocol.id} className="text-center align-middle">
            <td>{index + 1}</td>
            <td>
              {/* Clicking the name opens the protocol modal (detail + edit) */}
              <ProtocolModal
                protocolObj={getProtocolForEditing(protocol)}
                clearSearchValue={clearSearchValue}
                onEditButtonHover={() => fetchFullProtocolData(protocol.id)}
                isEditButtonLoading={loadingProtocols[protocol.id]}
                onSuccessfulSubmit={() => {
                  invalidateProtocol(protocol.id);
                  handleDelete();
                }}
                triggerContent={protocol.name}
                triggerVariant="link"
                triggerClassName="p-0 text-start"
              />
            </td>
            <td>{renderProductPreview(protocol)}</td>
            <td>
              {protocol.url ? (
                <a
                  href={protocol.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-inline-flex justify-content-center"
                  aria-label={`Open the external document for ${protocol.name}`}
                >
                  <NavigateToUrlIcon iconSize="1.2rem" />
                </a>
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
            {!isViewer && (
            <td className="d-flex flex-row align-items-center justify-content-evenly">
              <div>
                <ProtocolModal
                  protocolObj={getProtocolForEditing(protocol)}
                  clearSearchValue={clearSearchValue}
                  onEditButtonHover={() => fetchFullProtocolData(protocol.id)}
                  isEditButtonLoading={loadingProtocols[protocol.id]}
                  onSuccessfulSubmit={() => {
                    invalidateProtocol(protocol.id);
                    handleDelete();
                  }}
                />
              </div>
              {/* Deleting a protocol never deletes the linked products */}
              <DeleteButton
                objectType="protocol"
                objectName={protocol.name}
                objectId={protocol.id}
                deleteFetchFunc={deleteProtocol}
                onSuccessfulDelete={handleDelete}
                clearSearchValue={clearSearchValue}
              />
            </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default ProtocolTable;
